import React from "react";
import { api } from "@/lib/api";
import { VALUES, SKILLS, skillLabel } from "@/lib/constants";
import { VOLUNTEER } from "@/constants/testIds";
import { SkillsCombobox } from "@/components/SkillsCombobox";
import { Check, Sparkles, X } from "lucide-react";

// Values + skills pickers for onboarding step 2 (extracted from
// Onboarding.jsx in the Phase 15 component split — behaviour unchanged).

export function ValuesSection({ type, values, onToggle }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">{type === "organisation" ? "Causes you support" : "What you value"}</label>
      <div className="flex flex-wrap gap-2 mt-3">
        {VALUES.map((v) => {
          const on = values.has(v.id);
          return (
            <button
              key={v.id}
              data-testid={VOLUNTEER.onboardingValueChip(v.id)}
              onClick={() => onToggle(v.id)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${on ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-700 hover:border-forest/40"}`}
            >
              {on && <Check className="w-3.5 h-3.5" />}{v.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function SkillsSection({ type, skills, suggestions, onToggle, onAdd }) {
  const extraSkills = [...skills].filter((s) => !SKILLS.includes(s));
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">{type === "organisation" ? "Skills you need" : "Skills you bring"}</label>
      {suggestions.length > 0 && type !== "organisation" && (
        <div
          data-testid="skill-suggestions"
          className="mt-3 bg-forest/5 border border-forest/15 rounded-xl p-3"
        >
          <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> AI suggestions
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {suggestions.map(({ skill, confidence, custom }) => {
              const already = skills.has(skill);
              return (
                <button
                  key={skill}
                  type="button"
                  data-testid={`skill-suggestion-${skill.replace(/\s+/g, '-')}`}
                  onClick={() => {
                    if (already) return;
                    onAdd(skill);
                    // Custom (outside taxonomy) suggestions follow the
                    // same admin-review queue path as manual adds.
                    if (custom) api.suggestSkill(skill).catch(() => {});
                  }}
                  disabled={already}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors capitalize ${
                    already
                      ? "bg-forest text-white border-forest opacity-70 cursor-default"
                      : custom
                        ? "bg-white border-dashed border-terracotta/60 text-terracotta hover:bg-terracotta hover:text-white"
                        : "bg-white border-forest/30 text-forest hover:bg-forest hover:text-white"
                  }`}
                  title={custom ? "Not in our skills list yet — adding it queues it for review" : (confidence != null ? `AI confidence: ${(confidence * 100).toFixed(0)}%` : undefined)}
                >
                  {skillLabel(skill)}{custom ? " · custom" : ""}
                  {already ? " ✓" : " +"}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        {SKILLS.map((s) => {
          const on = skills.has(s);
          return (
            <button
              key={s}
              data-testid={VOLUNTEER.onboardingSkillChip(s)}
              onClick={() => onToggle(s)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors capitalize ${on ? "bg-terracotta text-white border-terracotta" : "bg-white border-neutral-200 text-neutral-700 hover:border-terracotta/50"}`}
            >
              {skillLabel(s)}
            </button>
          );
        })}
      </div>
      {/* Phase 7: skills picked via search/custom-add, beyond the quick-pick chips */}
      {extraSkills.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="extra-skills-row">
          {extraSkills.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`extra-skill-chip-${s.replace(/\s+/g, "-")}`}
              onClick={() => onToggle(s)}
              title="Click to remove"
              className="text-sm px-3 py-1.5 rounded-full border bg-forest text-white border-forest capitalize inline-flex items-center gap-1.5 hover:bg-forest-hover"
            >
              {skillLabel(s)} <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}
      <div className="mt-3">
        <SkillsCombobox selected={skills} onAdd={onAdd} />
      </div>
    </div>
  );
}
